import { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { exportExcel, exportPDF } from "../lib/exporters";
import { LanguageFields } from "./LanguageFields";
import { toast } from "sonner";
import { Languages, Search, FileSpreadsheet, FileText, ChevronLeft, ChevronRight, Users, CalendarDays, MessageCircle, Pencil, Phone, PhoneOff, X, Send, Power, PowerOff, CalendarPlus } from "lucide-react";

const btn = "inline-flex items-center gap-1.5 border border-line rounded-md px-3 py-1.5 text-sm font-semibold disabled:opacity-40";
const inp = "w-full rounded-md border border-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-clinical/30";
const iso = (d) => d.toISOString().slice(0, 10);
function mondayOf(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // 0 = Monday
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}

function MessageDialog({ interpreter, onClose }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!body.trim()) return toast.error("Type a message first.");
    setSending(true);
    try { await api.post("/messages", { to_user_id: interpreter.id, body: body.trim() }); toast.success(`Message sent to ${interpreter.name}`); onClose(); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="dir-message-dialog">
      <div className="bg-white rounded-lg w-full max-w-md p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold text-ink flex items-center gap-2"><MessageCircle className="w-5 h-5 text-clinical" /> Message {interpreter.name}</h3>
          <button onClick={onClose} data-testid="dir-message-close" className="text-muted2 hover:text-ink"><X className="w-5 h-5" /></button>
        </div>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="Write a message…" className={inp} data-testid="dir-message-body" />
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={onClose} className={btn}>Cancel</button>
          <button onClick={send} disabled={sending} data-testid="dir-message-send" className="inline-flex items-center gap-1.5 bg-clinical text-white rounded-md px-4 py-2 text-sm font-semibold disabled:opacity-50"><Send className="w-4 h-4" /> Send</button>
        </div>
      </div>
    </div>
  );
}

function EditDialog({ interpreter, onClose, onSaved }) {
  const [phone, setPhone] = useState(interpreter.phone || "");
  const [languages, setLanguages] = useState(interpreter.verified_languages || interpreter.languages || []);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!phone.trim()) return toast.error("A phone number is required for interpreters.");
    if (!languages.length) return toast.error("Select at least one verified language.");
    setSaving(true);
    try { await api.put(`/admin/users/${interpreter.id}`, { phone: phone.trim(), languages }); toast.success("Interpreter updated"); onSaved(); onClose(); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="dir-edit-dialog">
      <div className="bg-white rounded-lg w-full max-w-md p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold text-ink flex items-center gap-2"><Pencil className="w-5 h-5 text-clinical" /> Edit {interpreter.name}</h3>
          <button onClick={onClose} data-testid="dir-edit-close" className="text-muted2 hover:text-ink"><X className="w-5 h-5" /></button>
        </div>
        <label className="block mb-3"><span className="text-xs uppercase tracking-wider font-bold text-muted2">Phone (required)</span>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} className={`${inp} mt-1`} data-testid="dir-edit-phone" placeholder="+263771234567" /></label>
        <div className="border border-line rounded-md p-3 bg-sand/40 mb-3">
          <LanguageFields languages={languages} interpreterLanguages={[]} showInterpreter={false} verifiedOnly
            onChange={({ languages }) => setLanguages(languages)} />
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className={btn}>Cancel</button>
          <button onClick={save} disabled={saving} data-testid="dir-edit-save" className="bg-clinical text-white rounded-md px-4 py-2 text-sm font-semibold disabled:opacity-50">Save</button>
        </div>
      </div>
    </div>
  );
}

function DirectoryCard() {
  const { user } = useAuth();
  const canEdit = user?.role === "system_admin" || user?.role === "super_admin";
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [msgTo, setMsgTo] = useState(null);
  const [editTo, setEditTo] = useState(null);
  const load = () => api.get("/admin/interpreter-directory", { params: { q } }).then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  const toggleStatus = async (r) => {
    const next = r.status === "active" ? "deactivated" : "active";
    if (next === "deactivated" && !window.confirm(`Deactivate ${r.name}? They will be signed out and cannot be assigned to new consultations.`)) return;
    try { await api.put(`/admin/users/${r.id}/status`, { status: next }); toast.success(next === "active" ? "Interpreter activated" : "Interpreter deactivated"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const COLS = [
    { key: "name", label: "Name" }, { key: "username", label: "Username" },
    { key: "phone", label: "Phone" }, { key: "langText", label: "Verified languages" },
    { key: "upcoming_bookings", label: "Upcoming bookings" }, { key: "availability_slots", label: "Availability slots" },
    { key: "status", label: "Status" },
  ];
  const exportRows = () => rows.map((r) => ({ ...r, langText: (r.verified_languages || []).join(", ") }));
  return (
    <div className="bg-white border border-line rounded-md p-5" data-testid="interpreter-directory">
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <h2 className="font-heading font-bold text-lg text-ink flex items-center gap-2"><Users className="w-5 h-5 text-clinical" /> Interpreter Directory</h2>
        <div className="flex gap-2">
          <button onClick={() => exportExcel("interpreter_directory", COLS, exportRows())} disabled={!rows.length} data-testid="dir-export-excel" className={`${btn} text-calm`}><FileSpreadsheet className="w-4 h-4" /> Excel</button>
          <button onClick={() => exportPDF("Interpreter Directory", COLS, exportRows())} disabled={!rows.length} data-testid="dir-export-pdf" className={`${btn} text-critical`}><FileText className="w-4 h-4" /> PDF</button>
        </div>
      </div>
      <div className="relative my-3">
        <Search className="w-4 h-4 text-muted2 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search interpreters by name…" data-testid="dir-search" className="w-full rounded-md border border-line pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-clinical/30" />
      </div>
      <div className="grid gap-2">
        {rows.map((r) => (
          <div key={r.id} data-testid="dir-row" className="flex flex-wrap items-center justify-between gap-2 border border-line rounded-md px-3 py-2.5">
            <div className="min-w-0">
              <span className="font-semibold text-ink text-sm">{r.name}</span>
              {r.username && <span className="text-muted2 text-xs ml-1">@{r.username}</span>}
              <p className="text-xs text-muted2 mt-0.5">{(r.verified_languages || []).join(", ") || "No verified languages"}</p>
              {r.phone
                ? <span data-testid="dir-whatsapp-yes" className="inline-flex items-center gap-1 mt-1 text-[11px] font-semibold text-calm"><Phone className="w-3 h-3" /> {r.phone} · WhatsApp on file</span>
                : <span data-testid="dir-whatsapp-no" className="inline-flex items-center gap-1 mt-1 text-[11px] font-semibold text-alert"><PhoneOff className="w-3 h-3" /> No phone — won't receive WhatsApp</span>}
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="font-bold text-clinical bg-clinical/10 rounded-full px-2 py-0.5">{r.upcoming_bookings} upcoming</span>
              <span className="text-muted2">{r.availability_slots} slots</span>
              {r.status !== "active" && <span className="text-alert font-semibold">{r.status}</span>}
              <button onClick={() => setMsgTo(r)} data-testid="dir-message-btn" title="Message" className="border border-line rounded-md p-1.5 hover:bg-sand text-clinical"><MessageCircle className="w-4 h-4" /></button>
              {canEdit && <button onClick={() => setEditTo(r)} data-testid="dir-edit-btn" title="Edit" className="border border-line rounded-md p-1.5 hover:bg-sand text-ink"><Pencil className="w-4 h-4" /></button>}
              {canEdit && (
                <button onClick={() => toggleStatus(r)} data-testid="dir-status-toggle" title={r.status === "active" ? "Deactivate" : "Activate"}
                  className={`border rounded-md p-1.5 ${r.status === "active" ? "border-alert/30 text-alert hover:bg-alert/10" : "border-calm/30 text-calm hover:bg-calm/10"}`}>
                  {r.status === "active" ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                </button>
              )}
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-muted2">No interpreters found.</p>}
      </div>
      {msgTo && <MessageDialog interpreter={msgTo} onClose={() => setMsgTo(null)} />}
      {editTo && <EditDialog interpreter={editTo} onClose={() => setEditTo(null)} onSaved={load} />}
    </div>
  );
}

function CalendarCard({ onBookDay }) {
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; }), [weekStart]);
  useEffect(() => {
    const start = iso(days[0]); const end = iso(days[6]);
    api.get("/admin/interpreter-availability", { params: { start, end } }).then(({ data }) => setSlots(data)).catch(() => {});
    api.get("/admin/interpreter-bookings", { params: { start, end } }).then(({ data }) => setBookings(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart]);
  const slotsByDate = useMemo(() => { const m = {}; slots.forEach((s) => { (m[s.date] = m[s.date] || []).push(s); }); return m; }, [slots]);
  const bookingsByDate = useMemo(() => { const m = {}; bookings.forEach((b) => { (m[b.date] = m[b.date] || []).push(b); }); return m; }, [bookings]);
  const shift = (n) => { const d = new Date(weekStart); d.setDate(d.getDate() + n * 7); setWeekStart(d); };
  const dow = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return (
    <div className="bg-white border border-line rounded-md p-5 mt-6" data-testid="interpreter-calendar">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <h2 className="font-heading font-bold text-lg text-ink flex items-center gap-2"><CalendarDays className="w-5 h-5 text-clinical" /> Availability Calendar</h2>
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} data-testid="cal-prev" className="border border-line rounded-md p-1.5 hover:bg-sand"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-semibold text-ink" data-testid="cal-week">{iso(days[0])} – {iso(days[6])}</span>
          <button onClick={() => shift(1)} data-testid="cal-next" className="border border-line rounded-md p-1.5 hover:bg-sand"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="flex items-center gap-4 mb-3 text-[11px] font-semibold">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-clinical/20 border border-clinical/40" /> Free slot</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-alert/20 border border-alert/40" /> Booked</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {days.map((d, i) => {
          const key = iso(d);
          const daySlots = (slotsByDate[key] || []).sort((a, b) => a.start_time.localeCompare(b.start_time));
          const dayBookings = (bookingsByDate[key] || []).sort((a, b) => a.time.localeCompare(b.time));
          return (
            <div key={key} data-testid="cal-day" className="border border-line rounded-md p-2 min-h-[90px] group">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-bold text-muted2">{dow[i]} <span className="font-normal">{key.slice(5)}</span></span>
                {onBookDay && (
                  <button onClick={() => onBookDay(key)} data-testid="cal-book-day" title={`Book an interpreter for ${key}`}
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-clinical hover:bg-clinical/10 rounded p-0.5">
                    <CalendarPlus className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="grid gap-1">
                {daySlots.map((s) => (
                  <div key={`s-${s.id}`} data-testid="cal-slot" className="text-[11px] bg-clinical/10 text-clinical rounded px-1.5 py-1 leading-tight">
                    <span className="font-semibold">{s.start_time}–{s.end_time}</span><br />{s.name}
                  </div>
                ))}
                {dayBookings.map((b) => (
                  <div key={`b-${b.id}`} data-testid="cal-booking" className="text-[11px] bg-alert/10 text-alert rounded px-1.5 py-1 leading-tight border border-alert/20">
                    <span className="font-semibold">{b.time} · booked</span><br />{b.interpreter_name}{b.language ? ` (${b.language})` : ""}
                  </div>
                ))}
                {daySlots.length === 0 && dayBookings.length === 0 && <span className="text-[11px] text-muted2">—</span>}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-muted2 mt-2">Green = declared free slots, amber = existing bookings. Booking is still allowed outside declared slots.</p>
    </div>
  );
}

function UtilisationCard() {
  const [range, setRange] = useState({ start: "", end: "" });
  const [util, setUtil] = useState({ interpreters: [], languages: [] });
  useEffect(() => {
    api.get("/admin/interpreter-utilisation", { params: { start: range.start, end: range.end } })
      .then(({ data }) => setUtil(data)).catch(() => {});
  }, [range]);
  const ICOLS = [{ key: "name", label: "Interpreter" }, { key: "langText", label: "Languages" }, { key: "bookings", label: "Bookings" }];
  const iExport = () => util.interpreters.map((r) => ({ ...r, langText: (r.languages || []).join(", ") }));
  const LCOLS = [{ key: "language", label: "Language" }, { key: "appointments", label: "Appointments" }, { key: "with_interpreter", label: "With interpreter" }, { key: "without_interpreter", label: "No interpreter" }];
  const suffix = range.start || range.end ? ` (${range.start || "…"} to ${range.end || "…"})` : "";
  return (
    <div className="bg-white border border-line rounded-md p-5 mt-6" data-testid="interpreter-utilisation-report">
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <h2 className="font-heading font-bold text-lg text-ink flex items-center gap-2"><Languages className="w-5 h-5 text-clinical" /> Interpreter Utilisation</h2>
        <div className="flex gap-2">
          <button onClick={() => exportExcel("interpreter_bookings", ICOLS, iExport())} disabled={!util.interpreters.length} data-testid="util-export-excel" className={`${btn} text-calm`}><FileSpreadsheet className="w-4 h-4" /> Excel</button>
          <button onClick={() => exportPDF("Interpreter Utilisation" + suffix, ICOLS, iExport())} disabled={!util.interpreters.length} data-testid="util-export-pdf" className={`${btn} text-critical`}><FileText className="w-4 h-4" /> PDF</button>
        </div>
      </div>
      <div className="flex items-end gap-2 flex-wrap my-3">
        <label className="block"><span className="text-xs uppercase tracking-wider font-bold text-muted2">From</span>
          <input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="util-start" className="block mt-1 rounded-md border border-line px-3 py-1.5 text-sm" /></label>
        <label className="block"><span className="text-xs uppercase tracking-wider font-bold text-muted2">To</span>
          <input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="util-end" className="block mt-1 rounded-md border border-line px-3 py-1.5 text-sm" /></label>
        {(range.start || range.end) && <button onClick={() => setRange({ start: "", end: "" })} data-testid="util-clear-range" className="text-xs font-semibold text-critical hover:underline pb-2">Clear</button>}
      </div>
      <h3 className="text-xs uppercase tracking-wider font-bold text-muted2 mb-2">Bookings per interpreter</h3>
      <div className="grid gap-2 mb-4">
        {util.interpreters.map((r) => (
          <div key={r.id} data-testid="util-interpreter-row" className="flex flex-wrap items-center justify-between gap-2 border border-line rounded-md px-3 py-2">
            <span className="font-semibold text-ink text-sm">{r.name} <span className="text-muted2 font-normal">· {(r.languages || []).join(", ") || "—"}</span></span>
            <span className="text-xs font-bold text-clinical">{r.bookings} booking{r.bookings === 1 ? "" : "s"}</span>
          </div>
        ))}
        {util.interpreters.length === 0 && <p className="text-sm text-muted2">No interpreter accounts yet.</p>}
      </div>
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="text-xs uppercase tracking-wider font-bold text-muted2">Language demand</h3>
        <div className="flex gap-2">
          <button onClick={() => exportExcel("language_demand", LCOLS, util.languages)} disabled={!util.languages.length} data-testid="lang-export-excel" className={`${btn} text-calm !py-1 !text-xs`}><FileSpreadsheet className="w-3.5 h-3.5" /> Excel</button>
          <button onClick={() => exportPDF("Language Demand" + suffix, LCOLS, util.languages)} disabled={!util.languages.length} data-testid="lang-export-pdf" className={`${btn} text-critical !py-1 !text-xs`}><FileText className="w-3.5 h-3.5" /> PDF</button>
        </div>
      </div>
      <div className="grid gap-2">
        {util.languages.map((r) => (
          <div key={r.language} data-testid="util-language-row" className="flex flex-wrap items-center justify-between gap-2 border border-line rounded-md px-3 py-2">
            <span className="font-semibold text-ink text-sm">{r.language}</span>
            <span className="text-xs text-muted2">{r.appointments} appt(s) · <span className="text-calm font-semibold">{r.with_interpreter} with</span> · {r.without_interpreter > 0 ? <span className="text-alert font-semibold">{r.without_interpreter} without interpreter</span> : "all covered"}</span>
          </div>
        ))}
        {util.languages.length === 0 && <p className="text-sm text-muted2">No language-specific appointments recorded yet.</p>}
      </div>
    </div>
  );
}

export function InterpreterReports({ onBookDay }) {
  return (
    <div className="mt-6" data-testid="interpreter-reports">
      <DirectoryCard />
      <CalendarCard onBookDay={onBookDay} />
      <UtilisationCard />
    </div>
  );
}
